import React from 'react';
import { useNavigate } from 'react-router-dom';
import BulletPointIcon from '../../icons/BulletPointIcon';
import { BusinessReadiness, BusinessStageType, MyBusinessSchema } from '../../data/types';

type GenAIResultViewProps = {
    readiness: BusinessReadiness | null;
    business: MyBusinessSchema | null;
    error?: string;
    wasUpdate?: boolean;
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setDreamModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const STAGE_LABELS: Record<BusinessStageType, string> = {
    dream: 'Dream Stage',
    build: 'Build Stage',
    scale: 'Scale Stage',
};

/** Render a saved business's structured readiness result. */
const GenAIResultView: React.FC<GenAIResultViewProps> = ({
    setIsOpen,
    readiness,
    business,
    error,
    wasUpdate,
    setDreamModalIsOpen,
}) => {
    const navigate = useNavigate();
    const stageLabel = readiness?.stage.label || STAGE_LABELS[business?.business_stage || 'dream'] || 'Business Stage';
    const benefits = readiness?.benefits || [];
    const checklistCount = readiness?.compliance.items.length || 0;
    const businessName = readiness?.business_name || business?.business_name || 'Your business';

    return (
        <div className="mx-auto flex min-h-[300px] flex-col items-center justify-center p-8">
            <div className="mb-6 w-full text-left">
                <h2 className="mb-3 text-center text-2xl font-bold text-[#0A3751]">
                    Business {wasUpdate ? 'Updated' : 'Added'} Successfully 🎉
                </h2>
                <p className="text-gray-700">
                    <span className="font-semibold text-[#0A3751]">{businessName}</span> has been classified under the{' '}
                    <span className="font-semibold text-[#0A3751]">{stageLabel}</span> based on the stage you submitted.
                </p>
                {readiness?.location && <p className="mt-1 text-sm text-gray-600">{readiness.location}</p>}
                {readiness?.summary && <p className="mt-4 text-gray-700">{readiness.summary}</p>}
                {error && <p className="mt-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">{error}</p>}
            </div>

            {benefits.length > 0 && (
                <ul className="mb-6 w-full space-y-3">
                    {benefits.map((benefit) => (
                        <li key={benefit} className="flex items-start gap-2">
                            <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#5DB6CE]">
                                <BulletPointIcon />
                            </div>
                            <span className="text-gray-700">{benefit}</span>
                        </li>
                    ))}
                </ul>
            )}

            <div className="mb-6 w-full rounded-xl border border-[#D5E3E8] bg-[#F7FBFC] p-4">
                <p className="font-semibold text-[#0A3751]">Compliance Readiness</p>
                <p className="mt-1 text-sm text-gray-700">
                    {checklistCount > 0
                        ? `${checklistCount} checklist items were generated from your business type and location.`
                        : 'Open My Business to generate or review your checklist.'}
                </p>
            </div>

            <div className="w-full space-y-3">
                <button
                    onClick={() => {
                        setIsOpen(false);
                        if (business?.id) navigate(`/business/${business.id}`);
                    }}
                    className="w-full py-3 px-4 bg-[#0A3751] text-white rounded-md hover:bg-[#0A3751]/90 transition-colors border-0 font-bold font-raleway text-[18px]"
                >
                    View My Business
                </button>
                <button
                    onClick={() => {
                        setIsOpen(false)
                        setDreamModalIsOpen(true)
                    }}
                    className="w-full py-3 px-4 text-[#0A3751] hover:underline transition-colors text-center border-0 font-semibold font-raleway text-[16px]"
                >
                    Go Back to Form
                </button>
            </div>
        </div>
    );
};

export default GenAIResultView;